"use client";

import { useMemo, useState } from "react";
import Image from "next/image";
import { useTranslations } from "next-intl";

import { cn } from "@/shared/lib/utils";
import { getChampionImageUrl } from "@/shared/lib/champion-image";
import { scoreColor } from "@/shared/lib/score-color";
import { RoleIcon } from "@/shared/ui/data-display/role-icon";
import { SectionHeading } from "./section-heading";

/**
 * KAN-877: таблица игроков турнира (эпик KAN-804).
 *
 * Заменяет `TournamentPlayerStatsBoard`. Разница не в ширине: прежняя таблица была
 * витриной, эта ещё и навигация — строка кликом выбирает игрока для профиля внизу
 * страницы, а балл в последней колонке связывает её с пьедесталом, MVP и бордами
 * поведения, где показан ТОТ ЖЕ балл (GL Score, KAN-764).
 *
 * Компонент чистый: данные приходят пропами, запросы делает контейнер
 * (`players-table-section.tsx`). Так таблицу можно проверять тестами без мока сети,
 * а страница итогов (KAN-878) отдаст ей уже загруженные ответы без второго запроса.
 */

/** Строка таблицы — плоский вид, собранный контейнером из двух ответов. */
export interface PlayersTableRow {
  userId: string;
  displayName: string;
  avatarUrl?: string | null;
  teamName?: string | null;
  /** Роль в турнире (KAN-805). Нет ни у кого — колонка роли скрывается целиком. */
  role?: string | null;
  games: number;
  wins: number;
  losses: number;
  /** Винрейт в процентах, 0..100 — так его отдаёт бэкенд. */
  winrate: number;
  /** Готовая строка «K/D/A» из бэкенда. */
  avgKda: string;
  /** Число для сортировки по KDA — строку сортировать нельзя. */
  kdaRatio: number;
  damagePerMin?: number | null;
  goldPerMin?: number | null;
  csPerMin?: number | null;
  champions: { championName: string; games: number }[];
  /** GL Score 0–100 внутри роли. Null — сравнивать не с чем либо реплеев нет. */
  score?: number | null;
  /** Почему балла нет. Пустой прочерк не отличает порог выборки от пропущенных игр. */
  scoreNote?: string | null;
}

/** Сколько иконок чемпионов помещается в колонку макета (88px). */
const MAX_CHAMPION_ICONS = 3;

/** Геометрия кольца балла: поле 38×38, радиус 15.5 — числа из макета. */
const RING_RADIUS = 15.5;
const RING_CIRCUMFERENCE = 2 * Math.PI * RING_RADIUS;

/**
 * Кольцо балла в строке. Балла нет — кольца нет: пустое кольцо с нулём читается как
 * «сыграл на ноль», хотя правда — «сравнивать не с чем». То же правило, что в
 * пьедестале (KAN-806).
 *
 * Третья копия кольца на странице (пьедестал, MVP, здесь) — они расходятся размером
 * и правилом цвета: у MVP кольцо фирменное, у остальных светофорное. Свести их в один
 * параметризованный компонент — работа сборки страницы, KAN-878.
 */
function ScoreRing({ score }: { score: number }) {
  const filled = Math.max(0, Math.min(100, score));
  const color = scoreColor(filled);

  return (
    <span className="relative block h-[38px] w-[38px] flex-none">
      <svg viewBox="0 0 38 38" className="absolute inset-0 -rotate-90">
        <circle
          cx="19"
          cy="19"
          r={RING_RADIUS}
          fill="none"
          stroke="var(--muted)"
          strokeWidth="3.5"
        />
        <circle
          cx="19"
          cy="19"
          r={RING_RADIUS}
          fill="none"
          stroke={color}
          strokeWidth="3.5"
          strokeLinecap="round"
          strokeDasharray={`${((filled / 100) * RING_CIRCUMFERENCE).toFixed(1)} ${RING_CIRCUMFERENCE.toFixed(1)}`}
        />
      </svg>
      <span className="absolute inset-0 flex items-center justify-center font-heading text-sm font-semibold leading-none tabular-nums">
        {Math.round(filled)}
      </span>
    </span>
  );
}

function ChampionIcon({ name }: { name: string }) {
  return (
    <span className="relative block h-[22px] w-[22px] shrink-0 overflow-hidden rounded">
      <Image
        src={getChampionImageUrl(name)}
        alt={name}
        title={name}
        width={22}
        height={22}
        className="size-full scale-[1.15] object-cover"
        unoptimized
        onError={(e) => {
          (e.target as HTMLImageElement).style.visibility = "hidden";
        }}
      />
    </span>
  );
}

/** Цвет винрейта — тот же светофор, что был в прежнем борде. */
function winrateClass(winrate: number): string {
  if (winrate >= 70) return "text-green-400";
  if (winrate >= 55) return "text-green-400/80";
  if (winrate >= 45) return "text-foreground";
  if (winrate >= 30) return "text-amber-400";
  return "text-red-400";
}

type ColumnKey =
  | "player"
  | "role"
  | "games"
  | "record"
  | "winrate"
  | "kda"
  | "damagePerMin"
  | "goldPerMin"
  | "csPerMin"
  | "champions"
  | "score";

interface Column {
  key: ColumnKey;
  /**
   * Ширина колонки. KAN-891: доля, а не пиксели.
   *
   * Раньше здесь стояли фиксированные ширины из макета, и сумма 862px при
   * `min-width:900px` означала, что на широком экране таблица не растягивалась —
   * колонки жались к левому краю, а справа оставалась пустота. В макете этого не
   * видно: он нарисован на ширине 1240px, где сумма почти совпадает с контейнером.
   *
   * Пропорции макета сохранены: `minmax(<минимум из макета>, <доля>)`. Минимум не
   * даёт колонке схлопнуться уже содержимого, доля отдаёт лишнюю ширину поровну
   * между числовыми колонками, а не одной первой.
   */
  width: string;
  align: "left" | "right" | "center";
  /** Число для сортировки. Нет функции — колонка не сортируется. */
  sortValue?: (row: PlayersTableRow) => number | null;
  /**
   * Есть ли у строки данные для этой колонки. Колонка, пустая у ВСЕХ игроков,
   * скрывается целиком: столбец прочерков занимает место и ничего не сообщает.
   * Колонки без этой функции показываются всегда.
   */
  hasData?: (row: PlayersTableRow) => boolean;
}

const COLUMNS: Column[] = [
  { key: "player", width: "minmax(214px, 2.4fr)", align: "left" },
  {
    key: "role",
    width: "minmax(56px, 0.7fr)",
    align: "left",
    hasData: (r) => Boolean(r.role && r.role.trim()),
  },
  { key: "games", width: "minmax(52px, 0.6fr)", align: "right", sortValue: (r) => r.games },
  { key: "record", width: "minmax(66px, 0.8fr)", align: "right", sortValue: (r) => r.wins },
  { key: "winrate", width: "minmax(52px, 0.6fr)", align: "right", sortValue: (r) => r.winrate },
  { key: "kda", width: "minmax(64px, 0.8fr)", align: "right", sortValue: (r) => r.kdaRatio },
  {
    key: "damagePerMin",
    width: "minmax(72px, 0.9fr)",
    align: "right",
    sortValue: (r) => r.damagePerMin ?? null,
    hasData: (r) => r.damagePerMin != null,
  },
  {
    key: "goldPerMin",
    width: "minmax(72px, 0.9fr)",
    align: "right",
    sortValue: (r) => r.goldPerMin ?? null,
    hasData: (r) => r.goldPerMin != null,
  },
  {
    key: "csPerMin",
    width: "minmax(60px, 0.7fr)",
    align: "right",
    sortValue: (r) => r.csPerMin ?? null,
    hasData: (r) => r.csPerMin != null,
  },
  {
    key: "champions",
    width: "minmax(88px, 1fr)",
    align: "center",
    hasData: (r) => r.champions.length > 0,
  },
  {
    key: "score",
    width: "minmax(62px, 0.8fr)",
    align: "right",
    sortValue: (r) => r.score ?? null,
    hasData: (r) => r.score != null,
  },
];

const ALIGN_CLASS: Record<Column["align"], string> = {
  left: "text-left",
  right: "text-right",
  center: "text-center",
};

/**
 * Сортировка. Пустая клетка уходит ВНИЗ при любом направлении, а не считается нулём:
 * «урона не измеряли» — это не «нанёс мало», и по возрастанию такие игроки возглавили
 * бы таблицу, ничего про себя не сообщив.
 */
function compareRows(
  a: PlayersTableRow,
  b: PlayersTableRow,
  sortValue: (row: PlayersTableRow) => number | null,
  ascending: boolean,
): number {
  const av = sortValue(a);
  const bv = sortValue(b);

  if (av == null && bv == null) return 0;
  if (av == null) return 1;
  if (bv == null) return -1;

  return ascending ? av - bv : bv - av;
}

function formatPerMin(value: number): string {
  return value >= 100 ? Math.round(value).toString() : value.toFixed(1);
}

export function PlayersTable({
  rows,
  selectedUserId,
  onSelectPlayer,
}: {
  rows: PlayersTableRow[];
  /** Игрок, показанный в профиле внизу страницы: его строка подсвечена. */
  selectedUserId?: string | null;
  onSelectPlayer?: (userId: string) => void;
}) {
  const t = useTranslations("TournamentStats.playersTable");

  /** Колонки, у которых есть данные хотя бы у одного игрока. */
  const columns = useMemo(
    () =>
      COLUMNS.filter(
        (column) => !column.hasData || rows.some((row) => column.hasData!(row)),
      ),
    [rows],
  );

  const scoreVisible = columns.some((column) => column.key === "score");

  // По умолчанию сортируем по баллу — ради него страницу и открыли. Балла нет
  // (турнир без реплеев) — по числу побед, как в прежнем борде.
  const [sortBy, setSortBy] = useState<ColumnKey | null>(null);
  const [ascending, setAscending] = useState(false);

  const activeSort: ColumnKey = sortBy ?? (scoreVisible ? "score" : "record");

  const sorted = useMemo(() => {
    const column = columns.find((c) => c.key === activeSort);
    if (!column?.sortValue) return rows;

    const { sortValue } = column;
    return [...rows].sort((a, b) => compareRows(a, b, sortValue, ascending));
  }, [rows, columns, activeSort, ascending]);

  if (rows.length === 0) return null;

  const template = columns.map((column) => column.width).join(" ");

  const handleSort = (key: ColumnKey) => {
    if (key === activeSort) {
      setAscending((prev) => !prev);
      return;
    }
    setSortBy(key);
    setAscending(false);
  };

  return (
    <section className="rounded-2xl border border-border bg-card px-[22px] pb-2 pt-[22px]">
      {/* Оговорка обязательна: без неё «74» в последней колонке читается как процент
          побед, а это сводная оценка внутри роли. */}
      <SectionHeading
        title={t("title")}
        hint={scoreVisible ? t("scoreHint") : undefined}
      />

      {/* Скролл внутри своего контейнера: страница по горизонтали не едет.
          Отрицательные поля возвращают скроллу всю ширину карточки. */}
      <div className="-mx-[22px] overflow-x-auto px-[22px]">
        <div className="min-w-[900px]" role="grid" aria-label={t("title")}>
          <div
            role="row"
            className="grid gap-x-3 border-b border-border px-2.5 pb-2.5"
            style={{ gridTemplateColumns: template }}
          >
            {columns.map((column) => {
              const sortable = Boolean(column.sortValue);
              const active = column.key === activeSort;
              const label = t(`column.${column.key}`);

              const className = cn(
                "text-[10px] font-bold uppercase tracking-[0.09em] text-muted-foreground",
                ALIGN_CLASS[column.align],
              );

              if (!sortable) {
                return (
                  <span key={column.key} role="columnheader" className={className}>
                    {label}
                  </span>
                );
              }

              return (
                <span
                  key={column.key}
                  role="columnheader"
                  // Направление объявляется на заголовке: иначе стрелка видна глазами,
                  // но для скринридера сортировки как будто нет.
                  aria-sort={
                    active ? (ascending ? "ascending" : "descending") : "none"
                  }
                  className={cn(
                    column.align === "right" && "justify-self-end",
                    column.align === "center" && "justify-self-center",
                  )}
                >
                  <button
                    type="button"
                    onClick={() => handleSort(column.key)}
                    aria-label={t("sortBy", { column: label })}
                    className={cn(
                      className,
                      "cursor-pointer transition-colors hover:text-foreground",
                      active && "text-foreground",
                    )}
                  >
                    {label}
                    {active ? (
                      <span aria-hidden className="ml-0.5">
                        {ascending ? "↑" : "↓"}
                      </span>
                    ) : null}
                  </button>
                </span>
              );
            })}
          </div>

          {sorted.map((row) => {
            const selected = row.userId === selectedUserId;

            return (
              <div
                key={row.userId}
                role="row"
                tabIndex={0}
                aria-selected={selected}
                onClick={() => onSelectPlayer?.(row.userId)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    onSelectPlayer?.(row.userId);
                  }
                }}
                className={cn(
                  "grid cursor-pointer items-center gap-x-3 border-b border-border px-2.5 py-[9px] transition-colors hover:bg-accent",
                  selected && "bg-accent",
                )}
                style={{ gridTemplateColumns: template }}
              >
                {columns.map((column) => (
                  <Cell key={column.key} column={column} row={row} />
                ))}
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

function Cell({ column, row }: { column: Column; row: PlayersTableRow }) {
  const numeric = cn("text-[13px] tabular-nums", ALIGN_CLASS[column.align]);

  switch (column.key) {
    case "player":
      return (
        <span role="gridcell" className="flex min-w-0 items-center gap-[9px]">
          <span className="flex h-[26px] w-[26px] flex-none items-center justify-center overflow-hidden rounded-full bg-muted text-[11px] font-bold text-muted-foreground">
            {row.avatarUrl ? (
              <Image
                src={row.avatarUrl}
                alt=""
                width={26}
                height={26}
                className="size-full object-cover"
                unoptimized
              />
            ) : (
              (row.displayName.trim().charAt(0) || "?").toUpperCase()
            )}
          </span>
          <span className="min-w-0">
            <span className="block truncate text-[13px] font-semibold">
              {row.displayName}
            </span>
            {row.teamName ? (
              <span className="block truncate text-[11px] text-muted-foreground">
                {row.teamName}
              </span>
            ) : null}
          </span>
        </span>
      );

    case "role":
      return (
        <span role="gridcell" className="flex items-center">
          {row.role ? <RoleIcon role={row.role} size={18} /> : null}
        </span>
      );

    case "games":
      return (
        <span role="gridcell" className={numeric}>
          {row.games}
        </span>
      );

    case "record":
      return (
        <span role="gridcell" className={cn(numeric, winrateClass(row.winrate))}>
          {row.wins}–{row.losses}
        </span>
      );

    case "winrate":
      return (
        <span role="gridcell" className={cn(numeric, winrateClass(row.winrate))}>
          {Math.round(row.winrate)}%
        </span>
      );

    case "kda":
      return (
        <span role="gridcell" className={cn(numeric, "font-semibold")}>
          {row.avgKda}
        </span>
      );

    case "damagePerMin":
    case "goldPerMin":
    case "csPerMin": {
      const value =
        column.key === "damagePerMin"
          ? row.damagePerMin
          : column.key === "goldPerMin"
            ? row.goldPerMin
            : row.csPerMin;

      // Прочерк, а не ноль: у этого игрока метрики нет, хотя у других по колонке есть.
      return (
        <span role="gridcell" className={cn(numeric, "text-muted-foreground")}>
          {value == null ? "—" : formatPerMin(value)}
        </span>
      );
    }

    case "champions":
      return (
        <span role="gridcell" className="flex justify-center gap-1">
          {row.champions.slice(0, MAX_CHAMPION_ICONS).map((champion) => (
            <ChampionIcon key={champion.championName} name={champion.championName} />
          ))}
        </span>
      );

    case "score":
      return (
        <span role="gridcell" className="flex items-center justify-end">
          {row.score == null ? (
            /* Прочерк без причины на проде читался как поломка: половина столбца
               пустая, и ничто не говорит, порог это или пропущенные данные. */
            <span
              className="cursor-help text-[13px] tabular-nums text-muted-foreground"
              title={row.scoreNote ?? undefined}
              aria-label={row.scoreNote ?? undefined}
            >
              —
            </span>
          ) : (
            <ScoreRing score={row.score} />
          )}
        </span>
      );
  }
}
